import {
  type MutableRefObject,
  type RefCallback,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const DEFAULT_SPRING_ANIMATION = {
  /**
   * A value from 0 to 1, on how much to damp the animation.
   * 0 means no damping, 1 means full damping.
   */
  damping: 0.7,
  /**
   * The stiffness of how fast/slow the animation gets up to speed.
   */
  stiffness: 0.05,
  /**
   * The inertial mass associated with the animation.
   * Higher numbers make the animation slower.
   */
  mass: 1.25,
} as const;

const STICK_TO_BOTTOM_OFFSET_PX = 70;
const SIXTY_FPS_INTERVAL_MS = 1000 / 60;

export interface UseStickToBottomOptions {
  /**
   * Whether to start at the bottom or scroll to there initially.
   * @default true
   */
  initialAtBottom?: boolean;
  /**
   * How to handle scrolling when the container or content is resized.
   * 'smooth' uses spring animation, 'instant' jumps immediately.
   * @default 'smooth'
   */
  resize?: 'smooth' | 'instant';
  /**
   * How to handle scrolling on the first content size change.
   * 'smooth' uses spring animation, 'instant' jumps immediately.
   * @default 'instant'
   */
  initial?: 'smooth' | 'instant';
}

export interface UseStickToBottomReturn {
  scrollRef: MutableRefObject<HTMLElement | null> & RefCallback<HTMLElement>;
  contentRef: MutableRefObject<HTMLElement | null> & RefCallback<HTMLElement>;
  scrollToBottom: () => Promise<void>;
  isAtBottom: boolean;
}

function useRefCallback<T extends (ref: HTMLElement | null) => void>(
  callback: T,
  deps: readonly unknown[],
) {
  const result = useCallback(
    (ref: HTMLElement | null) => {
      (result as MutableRefObject<HTMLElement | null>).current = ref;
      return callback(ref);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  ) as MutableRefObject<HTMLElement | null> & RefCallback<HTMLElement>;

  return result;
}

export function useStickToBottom(
  options: UseStickToBottomOptions = {},
): UseStickToBottomReturn {
  const {
    initialAtBottom = true,
    resize = 'smooth',
    initial = 'instant',
  } = options;

  const [isAtBottom, setIsAtBottom] = useState(initialAtBottom);
  const wasAtBottomBeforeResizeRef = useRef(initialAtBottom);

  const scrollRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const scrollResizeObserverRef = useRef<ResizeObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const velocityRef = useRef(0);
  const accumulatedRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);
  const isScrollingRef = useRef(false);
  const previousHeightRef = useRef<number | undefined>(undefined);
  const previousScrollClientHeightRef = useRef<number | undefined>(undefined);
  const lastScrollTopRef = useRef<number | undefined>(undefined);
  const expectedScrollTopRef = useRef<number | undefined>(undefined);
  const isFirstContentChangeRef = useRef(true);

  const getTargetScrollTop = useCallback(() => {
    if (!scrollRef.current) {
      return 0;
    }
    return scrollRef.current.scrollHeight - scrollRef.current.clientHeight;
  }, []);

  const getScrollDifference = useCallback(() => {
    if (!scrollRef.current) {
      return 0;
    }
    return getTargetScrollTop() - scrollRef.current.scrollTop;
  }, [getTargetScrollTop]);

  const checkIsNearBottom = useCallback(() => {
    return getScrollDifference() <= STICK_TO_BOTTOM_OFFSET_PX;
  }, [getScrollDifference]);

  const scrollToBottomInstant = useCallback(() => {
    if (!scrollRef.current) {
      return;
    }
    const targetScrollTop = getTargetScrollTop();
    scrollRef.current.scrollTop = targetScrollTop;
    setIsAtBottom(true);
    wasAtBottomBeforeResizeRef.current = true;
  }, [getTargetScrollTop]);

  const scrollToBottom = useCallback(async () => {
    if (!scrollRef.current) {
      return;
    }

    setIsAtBottom(true);
    isScrollingRef.current = true;

    const animate = () => {
      if (!scrollRef.current || !isScrollingRef.current) {
        isScrollingRef.current = false;
        return;
      }

      const tick = performance.now();
      const tickDelta =
        (tick - (lastTickRef.current ?? tick)) / SIXTY_FPS_INTERVAL_MS;
      lastTickRef.current = tick;

      const scrollTop = scrollRef.current.scrollTop;
      const targetScrollTop = getTargetScrollTop();
      const scrollDifference = targetScrollTop - scrollTop;

      if (scrollDifference <= 0) {
        // We're at or past the bottom
        scrollRef.current.scrollTop = targetScrollTop;
        isScrollingRef.current = false;
        expectedScrollTopRef.current = undefined;
        setIsAtBottom(true);
        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        return;
      }

      // Spring animation
      velocityRef.current =
        (DEFAULT_SPRING_ANIMATION.damping * velocityRef.current +
          DEFAULT_SPRING_ANIMATION.stiffness * scrollDifference) /
        DEFAULT_SPRING_ANIMATION.mass;

      accumulatedRef.current += velocityRef.current * tickDelta;

      const newScrollTop = scrollTop + accumulatedRef.current;
      scrollRef.current.scrollTop = newScrollTop;
      expectedScrollTopRef.current = newScrollTop;

      if (scrollRef.current.scrollTop !== scrollTop) {
        accumulatedRef.current = 0;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Stop any existing animation
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Reset animation state
    velocityRef.current = 0;
    accumulatedRef.current = 0;
    lastTickRef.current = null;
    expectedScrollTopRef.current = undefined;

    animate();
  }, [getTargetScrollTop]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) {
      return;
    }

    const scrollTop = scrollRef.current.scrollTop;
    const lastScrollTop = lastScrollTopRef.current ?? scrollTop;
    lastScrollTopRef.current = scrollTop;

    const targetScrollTop = getTargetScrollTop();
    const scrollDifference = targetScrollTop - scrollTop;
    const isNearBottom = scrollDifference <= STICK_TO_BOTTOM_OFFSET_PX;

    // Update the ref that tracks if we were at bottom (for ResizeObserver)
    wasAtBottomBeforeResizeRef.current = isNearBottom;

    // If we're programmatically scrolling, check if user is interfering
    if (isScrollingRef.current) {
      const expectedScrollTop = expectedScrollTopRef.current;

      // If the actual scroll position is significantly different from what we expected,
      // or if the user scrolled up (scrollTop decreased), cancel the animation
      if (
        expectedScrollTop !== undefined &&
        (scrollTop < expectedScrollTop - 1 || scrollTop < lastScrollTop)
      ) {
        isScrollingRef.current = false;
        expectedScrollTopRef.current = undefined;
        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        setIsAtBottom(false);
        wasAtBottomBeforeResizeRef.current = false;
        return;
      }
      // Otherwise, let the animation continue
      return;
    }

    // Normal scroll handling when not animating
    // If user scrolls up, we're not at bottom
    if (scrollDifference > STICK_TO_BOTTOM_OFFSET_PX) {
      setIsAtBottom(false);
      wasAtBottomBeforeResizeRef.current = false;
    } else if (isNearBottom) {
      setIsAtBottom(true);
      wasAtBottomBeforeResizeRef.current = true;
    }
  }, [getTargetScrollTop]);

  const handleScrollContainerResize = useCallback(() => {
    if (!scrollRef.current) {
      return;
    }

    // Use requestAnimationFrame to ensure layout has completed
    requestAnimationFrame(() => {
      if (!scrollRef.current) {
        return;
      }

      // Check if we were at the bottom before this resize
      const wasAtBottom = wasAtBottomBeforeResizeRef.current;

      // Check current scroll position
      const scrollTop = scrollRef.current.scrollTop;
      const targetScrollTop = getTargetScrollTop();
      const scrollDifference = targetScrollTop - scrollTop;
      const isCurrentlyNearBottom =
        scrollDifference <= STICK_TO_BOTTOM_OFFSET_PX;

      // If we were at the bottom before container was resized, stay at bottom
      if (wasAtBottom && !isScrollingRef.current && scrollDifference > 0) {
        if (resize === 'instant') {
          scrollToBottomInstant();
        } else {
          scrollToBottom();
        }
      } else if (isCurrentlyNearBottom) {
        setIsAtBottom(true);
        wasAtBottomBeforeResizeRef.current = true;
      }
    });
  }, [scrollToBottom, scrollToBottomInstant, getTargetScrollTop, resize]);

  const scrollRefCallback = useRefCallback(
    (scroll: HTMLElement | null) => {
      scrollRef.current?.removeEventListener('scroll', handleScroll);

      // Disconnect previous scroll container ResizeObserver
      if (scrollResizeObserverRef.current) {
        scrollResizeObserverRef.current.disconnect();
      }

      scrollRef.current = scroll;

      if (scroll) {
        scroll.addEventListener('scroll', handleScroll, { passive: true });

        // Create ResizeObserver to detect scroll container size changes
        scrollResizeObserverRef.current = new ResizeObserver(() => {
          if (!scrollRef.current) {
            return;
          }

          const currentClientHeight = scrollRef.current.clientHeight;
          const previousClientHeight = previousScrollClientHeightRef.current;

          // Only handle resize if clientHeight actually changed
          if (
            previousClientHeight !== undefined &&
            currentClientHeight !== previousClientHeight
          ) {
            handleScrollContainerResize();
          }

          previousScrollClientHeightRef.current = currentClientHeight;
        });

        scrollResizeObserverRef.current.observe(scroll);

        // Initial scroll to bottom if requested
        if (initialAtBottom) {
          // Use double RAF to ensure DOM and layout are ready
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (scroll) {
                if (initial === 'smooth') {
                  // Use smooth scroll for initial positioning
                  scrollToBottom();
                } else {
                  // Instant scroll
                  const target = scroll.scrollHeight - scroll.clientHeight;
                  scroll.scrollTop = target;
                }
                previousScrollClientHeightRef.current = scroll.clientHeight;
              }
            });
          });
        } else {
          previousScrollClientHeightRef.current = scroll.clientHeight;
        }
      }
    },
    [
      handleScroll,
      initialAtBottom,
      initial,
      scrollToBottom,
      getTargetScrollTop,
      handleScrollContainerResize,
    ],
  );

  const handleContentChange = useCallback(() => {
    if (!scrollRef.current || !contentRef.current) {
      return;
    }

    // Use requestAnimationFrame to ensure layout has completed
    requestAnimationFrame(() => {
      if (!scrollRef.current || !contentRef.current) {
        return;
      }

      // Check if we were at the bottom before this change
      const wasAtBottom = wasAtBottomBeforeResizeRef.current;

      // Check current scroll position
      const scrollTop = scrollRef.current.scrollTop;
      const targetScrollTop = getTargetScrollTop();
      const scrollDifference = targetScrollTop - scrollTop;
      const isCurrentlyNearBottom =
        scrollDifference <= STICK_TO_BOTTOM_OFFSET_PX;

      // If we were at the bottom before content was added, scroll to bottom
      // Also check if we're currently near bottom as a fallback
      if (
        (wasAtBottom || isCurrentlyNearBottom) &&
        !isScrollingRef.current &&
        scrollDifference > 0
      ) {
        // Use 'initial' for first content change, 'resize' for subsequent changes
        const scrollMethod = isFirstContentChangeRef.current ? initial : resize;
        if (scrollMethod === 'instant') {
          scrollToBottomInstant();
        } else {
          scrollToBottom();
        }
        // Mark that we've handled the first content change
        isFirstContentChangeRef.current = false;
      } else if (isCurrentlyNearBottom) {
        setIsAtBottom(true);
        wasAtBottomBeforeResizeRef.current = true;
        // Mark that we've handled the first content change if we're already at bottom
        isFirstContentChangeRef.current = false;
      }
    });
  }, [
    scrollToBottom,
    scrollToBottomInstant,
    getTargetScrollTop,
    initial,
    resize,
  ]);

  const contentRefCallback = useRefCallback(
    (content: HTMLElement | null) => {
      contentRef.current = content;

      // Disconnect previous observers
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }

      if (!content) {
        // Reset first content change flag when content is cleared
        isFirstContentChangeRef.current = true;
        return;
      }

      // Create ResizeObserver to detect size changes
      resizeObserverRef.current = new ResizeObserver(([entry]) => {
        const { height } = entry.contentRect;
        const difference = height - (previousHeightRef.current ?? height);
        previousHeightRef.current = height;

        if (!scrollRef.current) {
          return;
        }

        // If content grew, check if we should scroll
        if (difference > 0) {
          handleContentChange();
        } else if (difference < 0) {
          // Content shrunk - check if we're now near bottom
          if (checkIsNearBottom()) {
            setIsAtBottom(true);
            wasAtBottomBeforeResizeRef.current = true;
          }
        }
      });

      // Create MutationObserver to detect DOM additions (even if size doesn't change immediately)
      mutationObserverRef.current = new MutationObserver((mutations) => {
        // Check if any nodes were added
        const hasAddedNodes = mutations.some(
          (mutation) => mutation.addedNodes.length > 0,
        );

        if (hasAddedNodes) {
          handleContentChange();
        }
      });

      resizeObserverRef.current.observe(content);
      mutationObserverRef.current.observe(content, {
        childList: true,
        subtree: true,
      });
    },
    [handleContentChange, checkIsNearBottom],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (scrollResizeObserverRef.current) {
        scrollResizeObserverRef.current.disconnect();
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
      scrollRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return {
    scrollRef: scrollRefCallback,
    contentRef: contentRefCallback,
    scrollToBottom,
    isAtBottom,
  };
}
